import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, Link } from "react-router-dom";
import { ROUTES } from "@/app/router/routes";
import styles from "./ErrorPage.module.css";

interface ErrorPageProps {
  status?: number;
}

export function ErrorPage({ status }: ErrorPageProps) {
  const error = useRouteError();

  // BUG-UI-V4-005 (ui/specs/bugs/ui-v4) — this errorElement previously
  // swallowed every route error silently (useRouteError() was only used for
  // 404/403 status classification below, never logged), so real crashes
  // (e.g. /admin/configuration/image-generation) left no trace in
  // console/Sentry. Log unconditionally so the next crash is diagnosable.
  useEffect(() => {
    if (error) {
      console.error("[ErrorPage] Route error:", error);

      // Stale chunk recovery: after a new deployment Vite renames JS chunks
      // (content-hash changes). Browsers that cached the old index.html will
      // try to lazy-load old hashes that no longer exist on the server,
      // producing "Failed to fetch dynamically imported module". Detect this
      // pattern and do a single hard reload to pick up the fresh index.html
      // and new chunk manifest. sessionStorage flag prevents an infinite
      // reload loop if the error persists for other reasons.
      const isChunkError =
        error instanceof TypeError &&
        error.message.toLowerCase().includes("failed to fetch dynamically imported module");
      if (isChunkError) {
        const RELOAD_KEY = "chunk_error_reloaded";
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          console.warn("[ErrorPage] Stale chunk detected — reloading to pick up new build...");
          window.location.reload();
          return;
        } else {
          // Already reloaded once — clear the flag so subsequent navigations
          // can attempt recovery again if a new deployment happens later.
          sessionStorage.removeItem(RELOAD_KEY);
        }
      }
    }
  }, [error]);

  let statusCode = status ?? 500;
  let title = "Something went wrong";
  let description = "An unexpected error occurred. Please try again.";

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    if (error.status === 404) {
      title = "Page not found";
      description = "The page you're looking for doesn't exist.";
    } else if (error.status === 403) {
      title = "Access denied";
      description = "You don't have permission to view this page.";
    }
  } else if (statusCode === 404) {
    title = "Page not found";
    description = "The page you're looking for doesn't exist.";
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <span className={styles.statusCode}>{statusCode}</span>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.description}>{description}</p>
        <div className={styles.actions}>
          <Link to={ROUTES.APP_HOME} className={styles.homeLink}>
            Go to Home
          </Link>
          <button
            onClick={() => window.history.back()}
            className={styles.backButton}
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}
